import { IconTriangleExclamation } from 'central-icons/IconTriangleExclamation';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import {
  Button,
  ButtonGroup,
  Dialog,
  FormField,
  Input,
  Link,
  SupportingText,
} from '@revelio/ui';

import sourceWhaleIcon from '../../../../assets/sourcewhale-icon.png';
import themedModalStyles from '../../../Modal/themedModal.module.css';
import styles from './source-whale-modals.module.css';
import layoutStyles from './source-whale-onboarding-modal.module.css';

interface SourceWhaleOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (apiKey: string) => void;
  savedApiKey: string | null;
}

export const SourceWhaleOnboardingModal = ({
  isOpen,
  onClose,
  onSave,
  savedApiKey,
}: SourceWhaleOnboardingModalProps) => {
  const [apiKey, setApiKey] = useState(savedApiKey ?? '');

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    setApiKey(savedApiKey ?? '');
  }, [savedApiKey, isOpen]);

  const hasSavedApiKey = Boolean(savedApiKey);

  const validateApiKey = (apiKey: string) => {
    // SourceWhale API key are 43 characters long as of Jan 2025.
    // Let's allow a reasonable range of lengths in case it changes.
    return /^[A-Za-z0-9_-]{20,100}$/.test(apiKey);
  };

  const handleSubmit = () => {
    if (!validateApiKey(apiKey)) {
      return;
    }

    onSave(apiKey);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      data-testid="source-whale-onboarding-modal"
      title={
        <div className={layoutStyles.iconLabel}>
          <img className={layoutStyles.icon} src={sourceWhaleIcon} alt="" />
          Connect your SourceWhale account
        </div>
      }
      size="sm"
      className={classNames(themedModalStyles.content, styles.noSelect)}
      footer={
        <ButtonGroup size="sm">
          <Button variant="link" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={!validateApiKey(apiKey)}
            data-testid="Save-submit"
          >
            Save
          </Button>
        </ButtonGroup>
      }
    >
      <ApiKeyAlert hasSavedApiKey={hasSavedApiKey} />

      <FormField
        label="SourceWhale API Key"
        htmlFor="sourcewhale-api-key"
        supportingText={
          <>
            <SupportingText>
              Don't know your API key? Go to{' '}
              <Link
                className={layoutStyles.link}
                href="https://sourcewhale.app/admin#settings"
                target="_blank"
                rel="noopener"
              >
                SourceWhale settings
              </Link>{' '}
              to generate your API Key.
            </SupportingText>
            <SupportingText>
              If you need help setting up your SourceWhale account, reach out to
              <Link
                className={layoutStyles.link}
                href="mailto:sales@reveliolabs.com?subject=SourceWhale account setup"
                target="_blank"
                rel="noopener"
              >
                {' '}
                sales
              </Link>
              .
            </SupportingText>
          </>
        }
      >
        <Input
          id="sourcewhale-api-key"
          data-testid="sourcewhale-api-key-input"
          size="sm"
          value={apiKey}
          placeholder="API key"
          onChange={(e) => {
            setApiKey(e.target.value);
          }}
        />
      </FormField>
    </Dialog>
  );
};

// This modal only appears when the user has no API key set, or when a simple request to the SourceWhale API fails.
// As such, it always displays either a warning or an error message.
const ApiKeyAlert = ({ hasSavedApiKey }: { hasSavedApiKey: boolean }) => (
  <div
    role="alert"
    className={classNames(styles.alert, !hasSavedApiKey && styles.alertWarning)}
  >
    <div className={layoutStyles.iconLabel}>
      <IconTriangleExclamation
        size={16}
        className={layoutStyles.warningGlyph}
        data-error={hasSavedApiKey || undefined}
      />
      {hasSavedApiKey
        ? 'Your SourceWhale API key may be invalid. Please check your API key or generate a new one if needed.'
        : `You haven't connected your SourceWhale account yet!`}
    </div>
  </div>
);
